import { AIRequestError, AWSTextAdapter } from 'ckeditor5-premium-features';

// ckeditor5-premium-features is a webpack external with no TS declarations.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const AWSTextAdapterBase: any = AWSTextAdapter;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const AWSRequestError: any = AIRequestError;

/**
 * Custom adapter that sends AI proxy requests with credentials: 'include'
 * so the browser attaches the session cookie for authentication.
 *
 * Extends AWSTextAdapter to reuse preparePrompt() for model-specific prompt
 * construction, but overrides sendRequest() to own the fetch call directly
 * rather than delegating to the parent's obfuscated _processProxyRequest.
 *
 * See: https://ckeditor.com/docs/ckeditor5/lts-v47/features/ai/ai-assistant/ai-assistant-integration.html#implement-custom-adapter
 */
export class QualioAWSTextAdapter extends AWSTextAdapterBase {
  static get pluginName(): string {
    return 'AWSTextAdapter';
  }

  async sendRequest(requestData: {
    query: string;
    context: string;
    onData: (content: string) => void;
    actionId: string;
  }): Promise<void> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const self = this as any;
    const apiUrl: string = self.editor.config.get(
      'ai.assistant.adapter.aws.apiUrl',
    );
    const requestParameters: Record<string, unknown> =
      self.editor.config.get('ai.assistant.adapter.aws.requestParameters') ??
      {};
    const { model, stream, ...rest } = requestParameters;

    const prompt: string = await self.preparePrompt(
      requestData.query,
      requestData.context,
      model,
      requestData.actionId,
    );

    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        prompt,
        model,
        stream,
        actionId: requestData.actionId,
        ...rest,
      }),
      signal: self.abortController.signal,
    });

    if (!response.ok) {
      throw new AWSRequestError('AI request failed.');
    }

    const data = await response.json();
    requestData.onData(data.completion);
  }
}
